import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { fetchActiveMembers, refreshActiveMembers } from "@store/active-members/active-members-slice-thunk";
import { removeMemberFromCompany, setMemberPermissionsInCompany, updateMemberRoleInWorkspace } from "@store/members/members-slice-thunk";
import { FaroCoreApiUtils } from "@stellar/core-api-client";

/**
 * Creates an entity adapter to store a map with all active members that counted in the plan.
 */
export const activeMembersAdapter = createEntityAdapter({
  selectId: member => FaroCoreApiUtils.Member.getMemberId(member)
});
const initialState = {
  ...activeMembersAdapter.getInitialState(),
  fetching: {
    status: "uninitialized"
  },
  isOutdated: false,
  refreshStatus: "uninitialized"
};

/** Marks the active members as outdated, once there are any to be outdated */
function markOutdated(state) {
  if (state.fetching.status === "succeeded") {
    state.isOutdated = true;
  }
}

/** Whether a refresh is the latest request for the active members, so that it may change them */
function isLatestRefresh(state, requestId) {
  return requestId === state.fetching.requestId;
}

/**
 * Slice to access state of loaded active members
 */
const activeMembersSlice = createSlice({
  name: "activeMembers",
  initialState,
  reducers: {
    /**
     * Accepts a single active member entity and adds or replaces it.
     *
     * @param state store state
     * @param entity Project to be set or added to the store.
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    setOne: activeMembersAdapter.setOne,
    /**
     * Accepts an array of member entities, and adds or replaces them.
     *
     * @param state store state
     * @param entity Projects to be set or added to the store.
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    setMany: activeMembersAdapter.setMany,
    /**
     * Removes all member entities from the store.
     *
     * @param state store state
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    removeAll: activeMembersAdapter.removeAll,
    /**
     * Accepts an array of member IDs, and removes each member entity with those IDs if they exist.
     *
     * @param state store state
     * @param entity Project Ids to be removed from the store.
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    removeMany: activeMembersAdapter.removeMany,
    /**
     * Accepts a single member IDs, and removes the member entity with that ID if it exists.
     *
     * @param state store state
     * @param entity Project Id to be removed from the store.
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    removeOne: activeMembersAdapter.removeOne,
    resetActiveMembersState: () => initialState,
    /**
     * Marks the active members as outdated after anything that may make a member an active user
     * or not, e.g. losing the editor role in a project, so that they are refreshed while shown.
     * Changing a seat or a workspace role marks them by itself.
     */
    invalidateActiveMembers: markOutdated
  },
  extraReducers(builder) {
    builder.addCase(fetchActiveMembers.pending, (state, action) => {
      state.fetching.status = "pending";
      state.fetching.requestId = action.meta.requestId;
    }).addCase(fetchActiveMembers.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.fetching.requestId) {
        return;
      }
      activeMembersAdapter.setAll(state, action.payload);
      state.fetching.status = "succeeded";
    }).addCase(fetchActiveMembers.rejected, (state, action) => {
      if (action.meta.requestId !== state.fetching.requestId) {
        return;
      }
      state.fetching.status = "rejected";
    })

    // A refresh keeps the members shown, but only the latest request may replace them, so that
    // an older refresh that arrives last does not bring back members that changed since
    .addCase(refreshActiveMembers.pending, (state, action) => {
      state.fetching.requestId = action.meta.requestId;
      state.isOutdated = false;
      state.refreshStatus = "pending";
    }).addCase(refreshActiveMembers.fulfilled, (state, action) => {
      // The active members were reset, fetched anew or refreshed again meanwhile
      if (state.fetching.status !== "succeeded" || !isLatestRefresh(state, action.meta.requestId)) {
        return;
      }
      activeMembersAdapter.setAll(state, action.payload);
      state.refreshStatus = "succeeded";
    }).addCase(refreshActiveMembers.rejected, (state, action) => {
      // The members stay as they were, whoever shows them reports the failure
      if (isLatestRefresh(state, action.meta.requestId)) {
        state.refreshStatus = "rejected";
      }
    }).addCase(removeMemberFromCompany.fulfilled, (state, action) => {
      activeMembersAdapter.removeOne(state, action.payload.identity);
    })

    // A Sphere XG seat or a workspace role decides whether a member is an active user, other
    // permissions such as creating groups or a SCENE seat do not
    .addCase(setMemberPermissionsInCompany.fulfilled, (state, action) => {
      if (action.meta.arg.permissions.some(({
        name
      }) => name === "activeSeat")) {
        markOutdated(state);
      }
    }).addCase(updateMemberRoleInWorkspace.fulfilled, markOutdated);
  }
});
export const {
  setOne,
  setMany,
  removeAll,
  removeMany,
  removeOne,
  resetActiveMembersState,
  invalidateActiveMembers
} = activeMembersSlice.actions;
export const activeMembersReducer = activeMembersSlice.reducer;